import React, { useId } from "react";

import { ResilienceReport } from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import Info from "../Info";
import { K, NotEnoughData, useHalfLabel, VALIDATION_PAPER } from "./parts";
import {
  DOMAINS,
  Domain,
  formatChange,
  formatCount,
  formatScore,
  previousHalf,
  trend,
} from "./resilience-view";
import RepeatLine from "./RepeatLine";
import ResilienceRead from "./ResilienceRead";
import ScoreRadar, { MINIMUM_DOMAINS } from "./ScoreRadar";
import styles from "./resilience.module.scss";

const Hero = ({ report }: { report: ResilienceReport }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const halfLabel = useHalfLabel();
  const headingId = useId();
  const everyone = report.scores.selected.all_staff;
  const index = everyone.index;
  const change = report.scores.change.index;
  const { employees } = report.activity;

  const domains: Partial<Record<Domain, number>> = {};
  DOMAINS.forEach((domain) => {
    const tile = everyone.domains[domain];
    if (!tile.suppressed) domains[domain] = tile.value;
  });

  return (
    <section className={styles["hero-card"]} aria-labelledby={headingId}>
      <div
        className={styles["hero"]}
        data-has-radar={Object.keys(domains).length >= MINIMUM_DOMAINS}
      >
        <div className={styles["hero-figures"]}>
          <div className={styles["hero-eyebrow"]}>
            <h3 id={headingId} className={styles["hero-title"]}>
              {t(`${K}.sections.hero`)}
            </h3>
            <a
              className={styles["hero-note"]}
              href={VALIDATION_PAPER}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(`${K}.validation.marker`)}
              <span className={styles["visually-hidden"]}>
                {" "}
                {t(`${K}.validation.noteLabel`)}
              </span>
            </a>
            <Info
              figure={t(`${K}.sections.hero`)}
              text={t(`${K}.hero.info`)}
              align="start"
            />
          </div>
          {index.suppressed ? (
            <NotEnoughData tone="dark" />
          ) : (
            <>
              <p className={styles["hero-value"]}>
                {formatScore(index.value, i18n.language)}
                <span className={styles["hero-out-of"]}>{t(`${K}.outOf`)}</span>
              </p>
              {!change.suppressed && (
                <p className={styles["hero-change"]}>
                  <span>
                    {t(`${K}.hero.change`, {
                      count: Math.abs(change.value),
                      change: formatChange(change.value, i18n.language),
                      period: halfLabel(previousHalf(report.period_value)),
                    })}
                  </span>
                  <span className={styles["hero-trend"]}>
                    {t(`${K}.hero.trend.${trend(change.value)}`)}
                  </span>
                </p>
              )}
            </>
          )}
          {!employees.suppressed && (
            <p className={styles["hero-basis"]}>
              {t(`${K}.hero.basedOn`, {
                count: employees.value,
                formatted: formatCount(employees.value, i18n.language),
              })}
            </p>
          )}
          <RepeatLine repeat={report.repeat} />
        </div>
        <ScoreRadar
          series={[
            {
              name: t(`${K}.hero.radarSeries`),
              values: domains,
              tone: "accent",
            },
          ]}
        />
      </div>
      <ResilienceRead report={report} />
    </section>
  );
};

export default Hero;
